<template>
    <div class="stepper-wrap">

        <!-- TOOLBAR -->
        <step-dots :step="step" :labels="stepLabels" @back="goBack"/>

        <!-- STEPPER WINDOW -->
        <v-window v-model="carousel" touchless>
            <!-- BASIC INFO -->
            <v-window-item>
                <basic-info-stepper @change="basicInfoData" stepper-mode ref="basicInfo"/>
            </v-window-item>
            <!-- PROFESSIONAL INFO -->
            <v-window-item>
                <professional-info @change="professionalInfoData" stepper-mode/>
            </v-window-item>
            <!-- OBJECTIVES -->
            <v-window-item>
                <choose-objectives @change="objectives = $event" stepper-mode
                    title-key="entrance.objectives_coach" subtitle-key="entrance.subtext_objectives_coach" />
            </v-window-item>
            <!-- PAYMENT METHOD -->
            <v-window-item v-if="paymentsEnabled">
                <template v-if="paymentSet">
                    <v-card color="transparent" flat>
                        <v-card-title>{{ $t('payment.payment_method') }}</v-card-title>
                        <v-card-text>
                            <payment-card :owner-id="currentUser && currentUser.uid || null"
                                          :payment-method="account.stripeAccount.paymentMethod"/>
                        </v-card-text>
                    </v-card>
                </template>
                <card-creator v-else ref="cardCreator" @success="onCreateSuccess" @cardData="onCardData"
                              :default-name="name" no-save-button stepper-mode/>
            </v-window-item>
            <!-- SELECT PLAN -->
            <v-window-item>
                <select-plan v-if="paymentsEnabled && !showTerms" @change="planData" with-coupon stepper-mode @coupon="couponId = $event"/>
                <v-fade-transition>
                    <div v-show="showTerms" class="terms-step">
                        <div class="terms-title">{{ $t('entrance.terms_step_title') }}</div>

                        <button type="button" class="terms-row" @click="openDoc('terms_and_conditions.pdf')">
                            <span class="terms-row-label">{{ $t('entrance.terms_and_conditions') }}</span>
                            <v-icon size="20" color="accent">mdi-open-in-new</v-icon>
                        </button>

                        <button type="button" class="terms-row" @click="openDoc('privacy_policy.pdf')">
                            <span class="terms-row-label">{{ $t('entrance.privacy_and_policy') }}</span>
                            <v-icon size="20" color="accent">mdi-open-in-new</v-icon>
                        </button>

                        <v-checkbox v-model="acceptTerms" :label="$t('entrance.accept_all')"
                                    color="secondary" hide-details :ripple="false" class="terms-check"/>
                    </div>
                </v-fade-transition>
            </v-window-item>
        </v-window>

        <div class="container-bottom">
            <v-btn @click="continueStepper" color="primary" depressed large block
                   class="black--text text-none font-weight-bold"
                   :disabled="disabledContinue" :loading="loadingCard">
                {{ carousel === lastStep && showTerms ? $t('common.finish') : $t('common.continue') }}
            </v-btn>
            <logout-button outlined color="txtSecondary" large block class="mt-3"/>
        </div>

        <v-overlay z-index="50" :value="overlay">
            <v-progress-circular color="primary" indeterminate size="64"/>
        </v-overlay>

        <kimsa-confirm
            ref="confirmModal"
            :title="$t('common.notice')"
            :content="contentConfirmMessage"
            :loading="overlay"
            :persistent="false"
            @confirm="finishStepper"
            :close-on-confirm="false"
        />

    </div>
</template>

<script>

import StepDots from "@/components/entrance/elements/StepDots";
import BasicInfoStepper from "@/components/entrance/elements/BasicInfoStepper";
import ProfessionalInfo from "@/components/common/ProfessionalInfo";
import ChooseObjectives from "@/components/common/ChooseObjectives";
import PaymentCard from "@/components/common/payment/PaymentCard";
import CardCreator from "@/components/common/payment/CardCreator";
import SelectPlan from "@/components/coach/SelectPlan";
import KimsaConfirm from "@/components/common/KimsaConfirm";

import firebase, {db} from '@/utils/Firebase'
import LogoutButton from "@/components/common/inputs/LogoutButton";
import {loadStripe} from '@stripe/stripe-js';
import {Browser} from '@capacitor/browser';

export default {
    components: {
        LogoutButton,
        StepDots,
        BasicInfoStepper,
        ProfessionalInfo,
        ChooseObjectives,
        PaymentCard,
        CardCreator,
        SelectPlan,
        KimsaConfirm,
    },
    data() {
        return {
            carousel: 0,
            overlay: false,
            valid: false,
            loadingCard: false,
            // Step 1
            name: '',
            phone: '',
            phoneCode: '',
            birthDate: '',
            gender: '',
            country: '',
            file: null,
            // Step 2
            description: '',
            experience: null,
            sport_areas: [],
            professional_preparation: '',
            // Step 3
            objectives: [],
            // Step 4
            card: null,
            // Step 5
            plan: null,
            couponId: null,
            showTerms: false,
            acceptTerms: false,
            cardSet: false,
        }
    },
    computed: {
        step(vm = this) {
            if (vm.showTerms) return vm.stepLabels.length + 1
            return vm.carousel + 1
        },
        stepLabels(vm = this) {
            const labels = [
                vm.$t('entrance.step_basic'),
                vm.$t('entrance.step_professional'),
                vm.$t('entrance.step_objective'),
            ]
            if (!vm.paymentsEnabled) return labels
            return [...labels, vm.$t('entrance.step_payment'), vm.$t('entrance.step_plan')]
        },
        lastStep(vm = this) {
            return vm.paymentsEnabled ? 4 : 3
        },
        disabledContinue(vm = this) {
            if (vm.carousel === vm.lastStep) {
                if (vm.paymentsEnabled && !vm.plan) return true
                if (!vm.showTerms) return false
                return !vm.acceptTerms
            }
            if (vm.paymentsEnabled && vm.carousel === 3) {
                if (vm.paymentSet) return false
                return !vm.cardSet
            }
            return false
        },
        paymentSet(vm = this) {
            return !!vm.account && !!vm.account.stripeAccount && !!vm.account.stripeAccount.paymentMethod
        },
        contentConfirmMessage(vm = this) {
            if (vm.plan?.isFree === true) return vm.$t('coach.confirm_plan_stepper_free')
            return vm.$t('coach.confirm_plan_stepper')
        }
    },
    methods: {
        basicInfoData(payload) {
            const vm = this
            vm.name = payload.name || ''
            vm.birthDate = payload.birthDate || ''
            vm.country = payload.country || ''
            vm.phone = payload.phone || ''
            vm.phoneCode = payload.phoneCode || ''
            vm.gender = payload.gender || ''
            vm.file = payload.file || null
        },
        professionalInfoData(payload) {
            const vm = this
            vm.description = payload.description || ''
            vm.experience = payload.experience || null
            vm.sport_areas = payload.sport_areas || []
            vm.professional_preparation = payload.professional_preparation || ''
        },
        onCardData(payload) {
            const vm = this
            vm.valid = !!(payload && payload.billing_details.name)
            vm.cardSet = !!(payload && payload.billing_details.name)
        },
        async onCreateSuccess() {
            const vm = this

            await db.collection('users').doc(vm.currentUser.uid).update({
                name: vm.name,
                phone: vm.phone
            })
        },
        subscribePlanCoach: firebase.functions().httpsCallable('httpPayments-subscribePlanCoach'),
        planData(plan) {
            const vm = this
            vm.plan = plan
            vm.valid = plan !== null;
        },
        async continueStepper() {
            const vm = this
            if (vm.carousel === 0) vm.$refs.basicInfo.emitNow()
            if (vm.carousel === 0 && (!vm.name || (!vm.phone || vm.phone.length < 8))) return vm.toast(vm.$t('entrance.required_name_phone'), 'error')
            if (vm.carousel === 0 && !vm.phoneCode) return vm.toast(vm.$t('entrance.required_phone_code'), 'error')
            if (vm.paymentsEnabled && vm.carousel === 3 && !vm.paymentSet) {
                if (!vm.cardSet) return vm.toast(vm.$t('payment.set_card_data'), 'error')
                vm.loadingCard = true
                let ok
                try {
                    ok = await vm.$refs.cardCreator.send()
                } finally {
                    vm.loadingCard = false
                }
                if (!ok) return
                return vm.carousel++
            }
            if (vm.carousel !== vm.lastStep) {
                vm.showTerms = !vm.paymentsEnabled && vm.carousel + 1 === vm.lastStep
                return vm.carousel++
            }
            if (!vm.showTerms) return vm.showTerms = true
            if (!vm.paymentsEnabled) return vm.finishStepper()
            await vm.showConfirmPlan()
        },
        showConfirmPlan() {
            const vm = this
            vm.$refs.confirmModal.show()
        },
        async finishStepper() {
            const vm = this
            let payload = {}
            vm.overlay = true

            // // Step 1
            if (vm.name) payload.name = vm.name
            if (vm.phone) payload.phone = vm.phone
            if (vm.phoneCode) payload.phoneCode = vm.phoneCode
            if (vm.birthDate) payload.birthDate = vm.birthDate
            if (vm.gender) payload.gender = vm.gender
            if (vm.country) payload.country = vm.country
            if (vm.file) {
                const img = await vm.$refs.basicInfo.uploadImg()
                payload.logo = {
                    url: img?.tempUrl || '',
                    savePath: img?.savePath || '',
                }
            }
            // Step 2
            if (vm.description) payload.description = vm.description
            if (vm.experience) payload.experience = vm.experience
            if (vm.sport_areas.length > 0) payload.sport_areas = vm.sport_areas.map(sport => sport.value)
            if (vm.professional_preparation) payload.professional_preparation = vm.professional_preparation
            // Step 3
            if (vm.objectives.length > 0) payload.objectives = JSON.parse(JSON.stringify(vm.objectives))
            // Step 4
            // Trigger cardCreator in carousel 3

            // console.log('payload', payload);

            await db.collection('users').doc(vm.currentUser.uid).update(payload)
            // Step 5
            if (vm.paymentsEnabled) await vm.setSubscriptionCoachPlan()
            vm.$emit('finish')
            vm.overlay = false
        },
        async setSubscriptionCoachPlan() {
            const vm = this
            let subscribePayload = {
                planId: vm.plan.priceId,
                isFree: vm.plan.isFree === true,
                addTrial: true,
                couponId: vm.couponId,
            }

            await vm.subscribePlanCoach(subscribePayload).then(async result => {
                if (result.data.requiresAction) {
                    const clientSecret = result.data.paymentIntentClientSecret
                    const stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLIC_KEY)
                    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret);
                    if (error) {
                        console.log('Error en la autenticación 3D Secure:', error.message);
                        vm.toast(`${vm.$t('payment.subscription_failed')}: 3DS`, 'error')
                    } else {
                        if (paymentIntent.status === 'succeeded') {
                            console.log('Pago completado con éxito');
                        }
                    }
                }
            }).catch(err => {
                console.log(`${vm.$t('payment.subscription_failed')}: ${err.message}`)
                const stripeCode = err.details && err.details.stripeCode
                const codeToKey = {
                    card_declined: 'payment.card_declined',
                    incorrect_number: 'payment.card_incorrect_number',
                    invalid_number: 'payment.card_incorrect_number',
                    expired_card: 'payment.card_expired',
                    invalid_cvc: 'payment.card_invalid_cvc',
                    incorrect_cvc: 'payment.card_invalid_cvc',
                    invalid_expiry_month: 'payment.card_invalid_expiry',
                    invalid_expiry_year: 'payment.card_invalid_expiry',
                    processing_error: 'payment.card_processing_error',
                }
                vm.toast(vm.$t(codeToKey[stripeCode] || 'payment.subscription_failed'), 'error')
            })
        },
        goBack() {
            const vm = this
            if (vm.carousel === 0) return vm.$router.push({ name: 'select-profile' })
            if (vm.carousel === vm.lastStep && vm.showTerms) {
                vm.showTerms = false
                if (vm.paymentsEnabled) return
                return vm.carousel--
            }
            return vm.carousel--
        },
        async openDoc(file) {
            await Browser.open({ url: `https://web.builderpro.app/docs/${file}` })
        },
    },
    watch: {
        carousel(val) {
            if (val === 3) this.valid = false
            if (val === 4) this.valid = false
            window.scrollTo(0, 0)
        },
    }
}
</script>

<style scoped>

.stepper-wrap {
    position: relative;
    width: 100%;
    max-width: 440px;
}

.container-bottom {
    margin-top: 24px;
}

.terms-step {
    margin-top: 8px;
}

.terms-title {
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.02em;
    padding-bottom: 16px;
}

.terms-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    margin-bottom: 10px;
    background: var(--v-surface-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 10px;
    font-family: inherit;
    cursor: pointer;
    transition: border-color .15s ease;
}

.terms-row:hover {
    border-color: var(--v-secondary-base);
}

.terms-row-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    text-align: left;
}

.terms-check {
    margin-top: 10px;
    padding-top: 0;
}

</style>